import React, { useEffect, useState } from 'react';
import getConfig from 'next/config';
import {
  createTheme,
  ThemeProvider,
  StyledEngineProvider,
  adaptV4Theme,
  CssBaseline,
} from '@quillbot/design-system';
import { theme, darkMode, litcharts, herokuapp } from 'config/theme';
// import { ThemeProvider as StylesThemeProvider } from '@mui/styles';
import Firestore from 'src/authentication/handlers/firestore';
import { getReduxStore, wrapper } from '@Common/redux/store';
import { AppProps, AppContext } from 'next/app';
import { ErrorBoundary } from 'core/libs/bugsnag';
import Bugsnag from '@bugsnag/js';
import CoreTrack from 'core/logger/track';
// import withRedux from 'next-redux-wrapper';

import {
  setPlatformAction,
  setLanguageAction,
  setExtensionAction,
  setTheme,
  setCountryCode,
  setIndependentTool,
  setPartnerCompany,
  setABExperiments,
  setSidebarStateChange,
} from 'src/_common/actions/commonActions';
import { detectAgent, detectLanguage, parseCookies } from 'core/utils';
import { DocumentContext } from 'next/document';
import { getKlaviyoInstance } from 'core/libs/klaviyo';

// Import Swiper styles
// import 'swiper/swiper.scss';
// import 'swiper/components/navigation/navigation.scss';
// import 'swiper/components/pagination/pagination.scss';

import '../assets/css/paraphraser.css';
import '../assets/css/new_design.css';
import '../assets/css/editor.css';
import 'assets/scss/tab.scss';

import {
  createUserAction,
  updateUserAdditionalDetailsAction,
} from 'src/authentication/actions/authenticationActions';
import { APP_SETTINGS, partnerCompanies } from 'config/constants';
import Footer from 'src/_common/components/footer/Footer';
import { firebaseConf } from 'config/firebase';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { setUserOnboardingFromCookie } from 'src/paraphraser/actions/paraModelActions';
import { ExtensionMetaData, RootState } from 'src/_common/models/redux/store';
import QuillLocalStorage from 'core/utils/localStorageClient';
import { extensionPlatformType, NativePlatform } from 'core/models/platform';
import { login } from 'src/authentication/handlers/authentication';
import { CredentialResponse, PromptMomentNotification } from '@Common/models/oneTap';
import Track from 'src/authentication/handlers/track';
import { ABTestContext } from '@Common/context/ABTestContext';
import createEmotionCache from 'config/createEmotionCache';
import { EmotionCache } from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { setGcLangauge } from 'src/grammar-checker/actions/controlActions';

const { publicRuntimeConfig } = getConfig();
const { serverRuntimeConfig } = getConfig();
const clientSideEmotionCache = createEmotionCache();

function QuillApp(props: AppProps & { emotionCache?: EmotionCache }) {
  const { Component, pageProps, emotionCache = clientSideEmotionCache } = props;
  const dispatch = useDispatch();
  // const savedTheme = QuillLocalStorage.getInstance().get('theme', 'default');
  // dispatch(setTheme(savedTheme)); // check
  const themeName = useSelector((rootState: RootState) => rootState?.common.theme);
  const independentTool = useSelector((rootState: RootState) => rootState?.common.independentTool);

  const themeObj: {
    [key: string]: any;
  } = {
    default: theme,
    dark: darkMode,
    litcharts,
    herokuapp,
    'scribbr.com': theme,
    'scribbr.de': theme,
    'scribbr.ch': theme,
    'scribbr.at': theme,
    'Scribbr QA': theme,
    'Scribbr dev': theme,
  };
  const router = useRouter();
  /* dispatch(setPlatformAction((pageProps as any).agent));
  dispatch(setLanguageAction((pageProps as any).lang)); */
  const platform = useSelector((rootState: RootState) => rootState.common.platform);
  const isAuthenticated = useSelector((state: RootState) => state.user?.isAuthenticated);
  const isSyncedWithDb = useSelector((state: RootState) => state.user?.syncedWithDb);

  const [abExperiments, setabExperiments] = useState(pageProps.abExperiments);

  const getPlatform = () => {
    if (
      platform.isExtension &&
      platform.extensionMetaData?.type &&
      platform.extensionMetaData.type in extensionPlatformType
    ) {
      return extensionPlatformType[platform.extensionMetaData.type];
    }

    if (platform.isAndroid || platform.native === NativePlatform.ANDROID) {
      return 'android-native';
    }

    return 'webapp';
  };
  useEffect(() => {
    if (
      isAuthenticated === false &&
      platform.isExtension === true &&
      !['/login', '/signup'].includes(router.pathname)
      // !(router.pathname.includes('/login') || router.pathname.includes('/signup'))
    ) {
      router.push('/login');
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const handleRouteChange = (url: string) => {
      CoreTrack.getInstance().gTagEvent('page_view', { url });
    };

    CoreTrack.getInstance().gTagEvent('page_view');

    router.events.on('routeChangeComplete', handleRouteChange);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events]);

  useEffect(() => {
    const parsedCookies = parseCookies(document.cookie) || {};

    if (isAuthenticated && isSyncedWithDb && parsedCookies.abID) {
      fetch('/worker/abtest-experiments')
        .then((res) => res.json())
        .then((res) => {
          /* Setting AB test data in state & in redux */
          setabExperiments((prev: unknown) =>
            JSON.stringify(prev) === JSON.stringify(res) ? prev : res
          );
          dispatch(setABExperiments(res));
        })
        // eslint-disable-next-line no-console
        .catch((err) => {
          Bugsnag.notify({
            name: 'AB experiments fetch error',
            message: 'Error received when fetching experiements for logged in user',
            errorClass: err?.toString(),
          });
        });
    }
  }, [isAuthenticated, isSyncedWithDb]);

  useEffect(() => {
    APP_SETTINGS.INIT_WINDOW_HEIGHT = window.innerHeight;

    window.addEventListener('INITIATE_FIREBASE', () => {
      window.firebase.initializeApp(firebaseConf);
      Firestore.getInstance().registerFirebaseListeners();
    });

    const gaHandler = () => {
      window.ga('set', 'dimension2', getPlatform());
    };

    if (typeof window.ga !== 'undefined') {
      gaHandler();
    } else {
      window.addEventListener('GA_SCRIPT_LOADED', gaHandler);
    }

    /* dispatch(
      updateUserAdditionalDetailsAction({
        premium: process.browser
          ? QuillLocalStorage.getInstance().get('premium', 'false') === 'true'
          : false,
        isAuthenticated: process.browser
          ? QuillLocalStorage.getInstance().get('authenticated', 'false') === 'true'
          : false,
      })
    );
    window.addEventListener('load', () => {
      getKlaviyoInstance();

      /*       const materialIcon = document.createElement('link');
      materialIcon.href = 'https://fonts.googleapis.com/icon?family=Material+Icons';
      materialIcon.rel = 'stylesheet';
      document.head.appendChild(materialIcon);
    }); */

    const jssStyles = document.querySelector('#jss-server-side');
    if (jssStyles && jssStyles.parentNode) jssStyles.parentNode.removeChild(jssStyles);
    if (window.NodeList && !NodeList.prototype.forEach) {
      NodeList.prototype.forEach = Array.prototype.forEach as unknown as (
        callbackfn: (value: Node, key: number, parent: NodeList) => void,
        thisArg?: Object
      ) => void;
    }
    if (typeof window.CustomEvent !== 'function') {
      const CustomEvent = function (
        event: string,
        params: { bubbles: boolean; cancelable: boolean; detail: null }
      ) {
        params = params || { bubbles: false, cancelable: false, detail: null };
        const evt = document.createEvent('CustomEvent');
        evt.initCustomEvent(event, params.bubbles, params.cancelable, params.detail);
        return evt;
      };
      window.CustomEvent = CustomEvent;
    }
    if (!Element.prototype.matches) {
      Element.prototype.matches =
        (Element.prototype as any).msMatchesSelector || Element.prototype.webkitMatchesSelector;
    }

    if (!Element.prototype.closest) {
      // eslint-disable-next-line func-names
      Element.prototype.closest = function (s: string) {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        let el = this;
        do {
          if (Element.prototype.matches.call(el, s)) return el;
          el = el.parentElement || (el.parentNode as Element);
        } while (el !== null && el.nodeType === 1);
        return null;
      };
    }

    const s = document.createElement('script');
    s.type = 'text/javascript';
    s.innerHTML =
      'console.logs = [];console.stderror = console.error.bind(console);console.error = function(){console.logs.push(...Array.from(arguments));console.stderror.apply(console, arguments);}';
    document.body.appendChild(s);
    if (QuillLocalStorage.getInstance().get('theme') === 'undefined') {
      if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        QuillLocalStorage.getInstance().set('theme', 'dark');
        dispatch(setTheme('dark'));
      }
    }

    // check if browser is chrome or not and is desktop useror nots
    if (platform.isChrome && platform.isDesktop && chrome?.runtime) {
      // check if chrome ext is installed or not
      chrome?.runtime?.sendMessage(
        publicRuntimeConfig.CHROME_EXTENSION_ID,
        { action: 'detect-extension' },
        (response) => {
          let chromeInstalled = false;
          if (chrome?.runtime?.lastError) {
            chromeInstalled = false;
          } else if (response.success) {
            chromeInstalled = true;
            QuillLocalStorage.getInstance().set(
              'extensionInfo',
              JSON.stringify({
                deviceID: response.deviceID,
                version: response.version,
              })
            );
          }
          dispatch(
            setPlatformAction({
              ...platform,
              isChromeExtInstalled: chromeInstalled,
            })
          );
          /**
           * Redirecting the user to homepage if any of the following cases is true
           * Extension is installed and user is trying to access the uninstallation page
           * Extension is not installed and user is trying to access the installation page
           */
          if (
            (router.pathname === '/exit-survey' && chromeInstalled) ||
            (router.pathname === '/extension-success' && !chromeInstalled)
          ) {
            router.push('/');
          }
        }
      );
    }

    if (abExperiments) {
      try {
        const abAmpData = Object.keys(abExperiments).map(
          (experimentName) => `${experimentName}-${abExperiments[experimentName]}`
        );

        if (abAmpData.length) {
          CoreTrack.getInstance().ampSetUserProperties({
            ABTestUI: abAmpData,
          });
        }
      } catch (err) {
        Bugsnag.notify({
          name: 'AB experiments amplitude error',
          message: 'AB test experiment not set amplitude',
        });
      }
    }

    return () => {
      document.body.removeChild(s);
      window.removeEventListener('GA_SCRIPT_LOADED', gaHandler);
    };
  }, []);

  useEffect(() => {
    // Google One Tap
    const handleGoogleAuth = (res: CredentialResponse) => {
      login('google', undefined, undefined, 'onetap', res.credential);
    };

    const excludeRoutes = ['/login', '/signup', '/exit-survey'];
    if (
      !platform.isExtension &&
      !isAuthenticated &&
      window.google &&
      !excludeRoutes.includes(router.pathname) &&
      !independentTool
    ) {
      const oneTapDiv = document.createElement('div');
      oneTapDiv.id = 'g_id_onload';
      oneTapDiv.style.position = 'fixed';
      oneTapDiv.style.top = '50px';
      oneTapDiv.style.right = '5px';
      oneTapDiv.style.zIndex = '1001';
      document.body.appendChild(oneTapDiv);

      window.google?.accounts?.id?.initialize({
        client_id: publicRuntimeConfig.GOOGLE_ONE_TAP_ID,
        callback: handleGoogleAuth,
        prompt_parent_id: 'g_id_onload',
        cancel_on_tap_outside: false,
      });
      window.google?.accounts?.id?.prompt((prompt: PromptMomentNotification) => {
        if (prompt.isDisplayed()) {
          Track.shownOneTap();
        }
      });
    } else {
      document.getElementById('g_id_onload')?.remove();
      window.google?.accounts?.id?.cancel();
    }
  }, [router.pathname, isAuthenticated]);

  /*
  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      'serviceWorker' in navigator &&
      window.workbox !== undefined
    ) {
      const wb = window.workbox;

      // https://developers.google.com/web/tools/workbox/reference-docs/latest/module-workbox-window.Workbox#events
      wb.addEventListener('installed', (event) => {
        console.log(`Event ${event.type} is triggered.`);
        console.log(event);
      });

      wb.addEventListener('controlling', (event) => {
        logger('WORKBOX', `Event ${event.type} is triggered.`);
      });

      wb.addEventListener('activated', (event) => {
        logger('WORKBOX', `Event ${event.type} is triggered.`);
      });

      // DO NOT REMOVE THIS COULD BE USED LATER WHEN WE WANT TO UPDATE WEBSITE WHEN USER IS LIVE
      const promptNewVersionAvailable = () => {
        if (confirm('A newer version of this web app is available, reload to update?')) {
          wb.addEventListener('controlling', () => {
            window.location.reload();
          });
          wb.messageSW({ type: 'SKIP_WAITING' });
        } else {
          logger(
            'WORKBOX',
            'User rejected to reload the web app, keep using old verion. New verion will be automatically load when user open the app next time.'
          );
        }
      };
      wb.addEventListener('waiting', promptNewVersionAvailable);
      wb.addEventListener('externalwaiting', promptNewVersionAvailable);

      wb.addEventListener('message', (event) => {
        logger('WORKBOX', `Event ${event.type} is triggered.`);
      });

      wb.addEventListener('redundant', event => {
        console.log(`Event ${event.type} is triggered.`)
        console.log(event)
      })
      wb.addEventListener('externalinstalled', event => {
        console.log(`Event ${event.type} is triggered.`)
        console.log(event)
      })
      wb.addEventListener('externalactivated', event => {
        console.log(`Event ${event.type} is triggered.`)
        console.log(event)
      })

      // never forget to call register as auto register is turned off in next.config.js
      wb.register();
    }
  }, []);
  */
  const pagesWithNoSupportForDarkMode = [
    '/citation-generator',
    '/co-writer',
    '/co-writer/home',
    '/extension-success',
    '/exit-survey',
    '/guides/paraphrasing',
    '/guides/ai-writing-assistant',
    '/plagiarism-checker',
  ];

  const getTheme = () => {
    const requiredTheme = pagesWithNoSupportForDarkMode.includes(router.pathname)
      ? createTheme(themeObj.default)
      : createTheme(themeObj[themeName]);

    if (partnerCompanies.includes(themeName)) return createTheme(themeObj[themeName]);
    return requiredTheme;
  };
  const App = (
    <>
      <StyledEngineProvider injectFirst>
        <CacheProvider value={emotionCache}>
          <ThemeProvider theme={getTheme()}>
            <CssBaseline />
            <ABTestContext.Provider value={abExperiments}>
              <Component {...pageProps} />
            </ABTestContext.Provider>
          </ThemeProvider>
        </CacheProvider>
      </StyledEngineProvider>
      <style jsx global>{`
        #__next {
          flex: 1;
        }
      `}</style>
    </>
  );

  return ErrorBoundary ? <ErrorBoundary>{App}</ErrorBoundary> : App;
}

QuillApp.getInitialProps = wrapper.getInitialAppProps((store) => async (appContext) => {
  const docCtx = appContext.ctx as DocumentContext;
  const parsedCookies = parseCookies(docCtx.req?.headers?.cookie) || {};
  let isExtensionAtServer = false;
  if (docCtx.pathname === '/extension') {
    isExtensionAtServer = true;
    let extensionType: ExtensionMetaData['type'] = 'chrome';
    if (docCtx.query) {
      if (docCtx.query.wordExt === '') {
        if (docCtx.query.msWord === '') extensionType = 'ms-word';
        else extensionType = 'google-docs';
      }
    }
    // Track.getInstance().ampSetOnce(categoryExtmap[extensionType], true);
    store.dispatch(setExtensionAction({ activated: true, type: extensionType }));
  }
  let partnerCompany = null;
  if (docCtx.pathname === '/grammar-check' || docCtx.pathname === '/citation-generator') {
    if (docCtx?.query?.independentTool === 'true') {
      store.dispatch(setIndependentTool(true));
      if (
        docCtx?.query?.partnerCompany &&
        partnerCompanies.includes(docCtx?.query?.partnerCompany as string)
      ) {
        partnerCompany = docCtx?.query?.partnerCompany;
      }

      if (docCtx?.query?.language) {
        const language = docCtx?.query?.language === 'DE' ? 'DE' : 'EN';
        if (language === 'DE') {
          store.dispatch(setGcLangauge(language));
        }
      }
    }
  }

  if (!process.browser) {
    let countryCode = docCtx.req?.headers['cf-ipcountry'] || '';
    countryCode = Array.isArray(countryCode) ? countryCode[0] : countryCode;
    const validNativeQuery = Object.values(NativePlatform);
    const agent = detectAgent(docCtx);
    const lang = detectLanguage(docCtx);
    let savedTheme = parsedCookies?.theme || 'default';
    if (partnerCompany) {
      savedTheme = partnerCompany as string;
      store.dispatch(setPartnerCompany(partnerCompany as string));
    }
    const nativeQuery = docCtx.query.native as NativePlatform;
    let native = docCtx.pathname === '/native' ? NativePlatform.ANDROID : undefined;
    if (!native && docCtx.query.native && validNativeQuery.includes(nativeQuery)) {
      native = nativeQuery;
    }
    store.dispatch(
      setPlatformAction({
        ...agent,
        native,
      })
    );
    store.dispatch(setLanguageAction(lang));
    const authState = parsedCookies?.authenticated === 'true';
    const premiumState = parsedCookies?.premium === 'true';
    const userOnboardingStatus = parsedCookies?.userOnboardingScenario !== 'false';
    const sidebarPinned = parsedCookies?.sidebarPinned === 'true';

    store.dispatch(createUserAction({ isAuthenticated: authState, premium: premiumState }));
    store.dispatch(setUserOnboardingFromCookie({ userOnboardingStatus }));
    store.dispatch(setTheme(savedTheme));
    store.dispatch(setCountryCode(countryCode));
    store.dispatch(setSidebarStateChange(sidebarPinned));
  }
  const xForwaredFor = (() => {
    if (!process.browser && docCtx.req?.headers['x-forwarded-for']) {
      if (typeof docCtx.req?.headers['x-forwarded-for'] === 'string') {
        return docCtx.req?.headers['x-forwarded-for']?.split(',').pop()?.trim();
      }
      if (typeof docCtx.req?.headers['x-forwarded-for'] === 'object') {
        return docCtx.req?.headers['x-forwarded-for'].pop()?.trim();
      }
    }
    return null;
  })();

  if (!process.browser) {
    docCtx.res?.setHeader('Cache-Control', 'no-cache, max-age=0, must-revalidate');
  }
  if (docCtx.pathname === '/native') {
    docCtx.res?.writeHead(302, {
      Location: '/?native=android',
      'Content-Type': 'text/html; charset=utf-8',
    });
    docCtx.res?.end();
  }

  const abExperiments = parsedCookies?.abExperiments
    ? JSON.parse(parsedCookies.abExperiments)
    : null;

  store.dispatch(setABExperiments(abExperiments));

  return {
    pageProps: {
      isExtensionAtServer,
      ip:
        xForwaredFor ||
        docCtx.req?.connection?.remoteAddress ||
        docCtx.req?.socket?.remoteAddress ||
        null,
      abExperiments,
    },
  };
});

export default wrapper.withRedux(QuillApp);
